// Propaganda Library Data
// Three interconnected collections: Materials, Organisations, Incidents
// Cross-linked via orgId fields

// ===== CATEGORY TAGS =====
export const materialTags = [
  'Leaflets & flyers',
  'Social media',
  'Talking points',
  'Media guides',
  'Training materials',
  'Lobby & advocacy',
  'Student activism',
  'Tech & apps',
  'Government programmes',
  'Video'
];

// ===== TACTIC TAGS =====
// Each grounded in established scholarly frameworks
export const tacticTags = [
  'Manufactured consent',    // Chomsky & Herman, Manufacturing Consent (1988)
  'Astroturfing',            // Political science: fake grassroots campaigns
  'Dehumanisation',          // Bandura (moral disengagement), Haslam, Kelman
  'False equivalence',       // Logical fallacy / media criticism
  'Historical erasure',      // Postcolonial & settler-colonial studies
  'Historical revisionism',  // Rewriting or distorting historical record
  'Normalisation',           // Critical theory / Palestine studies
  'Deflection',              // Rhetorical technique (whataboutism, red herring)
  'Emotional manipulation',  // Argumentum ad passiones / appeal to emotion
  'Appeal to security',      // Securitisation theory, Copenhagen School of IR
  'Victim framing',          // DARVO: Deny, Attack, Reverse Victim and Offender
  'Censorship & suppression',
  'Disinformation',          // Deliberate spread of false information
  'Colonisation marketing'   // Marketing colonial practices as charity/environmentalism
];

// ===== INCIDENT TYPES =====
export const incidentTypes = [
  'Censorship',
  'Cultural erasure',
  'Academic suppression',
  'Media pressure',
  'Legislative',
  'Institutional capture',
  'Blacklisting',
  'Journalist targeting',
  'Media suppression'
];

// ===== INCIDENT CURRENT STATE =====
// Simple indicator of where things stand right now
// The update timeline provides the full picture
export const incidentStates = [
  'Currently in effect',
  'Overturned',
  'Developing'
];

// ===== ORGANISATIONS =====
export const orgsData = [
  {
    id: 'aipac',
    slug: 'aipac',
    name: 'American Israel Public Affairs Committee (AIPAC)',
    fullName: 'American Israel Public Affairs Committee',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '1963',
    description: 'The most powerful pro-Israel lobby in the US. Distributes regular policy briefs and talking points to lawmakers and media contacts, funds political campaigns through its PAC, and pressures legislators to support unconditional military aid to Israel. Has spent hundreds of millions on US elections.',
    tags: ['Lobbying'],
    relatedOrgs: ['dmfi', 'cufi', 'icc'],
    website: 'https://www.aipac.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.aipac.org',
    sources: [
      { name: 'OpenSecrets: AIPAC spending data', url: 'https://web.archive.org/web/2024/https://www.opensecrets.org/political-action-committees-pacs/aipac/C00104299/summary/2024' }
    ]
  },
  {
    id: 'camera',
    slug: 'camera',
    name: 'Committee for Accuracy in Middle East Reporting in America (CAMERA)',
    fullName: 'Committee for Accuracy in Middle East Reporting in America',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '1982',
    description: 'Media watchdog that organises mass letter-writing and complaint campaigns against journalists and outlets perceived as critical of Israel. Has successfully pressured newsrooms to alter coverage, retract stories, and discipline reporters. Operates an \'action alert\' system mobilising supporters to target specific articles and journalists.',
    tags: ['Media'],
    relatedOrgs: ['honest-reporting'],
    website: 'https://www.camera.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.camera.org',
    sources: []
  },
  {
    id: 'standwithus',
    slug: 'standwithus',
    name: 'StandWithUs',
    fullName: 'StandWithUs',
    country: 'United States',
    countryCode: 'US',
    scope: 'International',
    founded: '2001',
    description: 'Pro-Israel campus propaganda operation that distributes pamphlets, social media kits, and graphics designed to demonise Palestinian resistance and sanitise Israeli military violence. Partially funded by the Israeli government\'s Ministry of Strategic Affairs, making it a direct arm of state-sponsored hasbara. Materials consistently frame legitimate resistance to military occupation as \'terrorism\' and portray the Israeli state as a victim of unprovoked aggression. Deploys trained student fellows on campuses to counter pro-Palestine organising and disrupt BDS campaigns.',
    tags: ['Campus'],
    relatedOrgs: ['icc', 'hillel'],
    website: 'https://www.standwithus.com',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.standwithus.com',
    sources: []
  },
  {
    id: 'honest-reporting',
    slug: 'honest-reporting',
    name: 'HonestReporting',
    fullName: 'HonestReporting',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '2000',
    description: 'Media monitoring organisation that mobilises supporters to flood newsrooms with complaints about coverage it considers anti-Israel. Has claimed credit for the firing and reassignment of journalists deemed too sympathetic to Palestinians. Runs a \'communal action\' model targeting individual reporters.',
    tags: ['Media'],
    relatedOrgs: ['camera'],
    website: 'https://honestreporting.com',
    archivedWebsite: 'https://web.archive.org/web/2024/https://honestreporting.com',
    sources: []
  },
  {
    id: 'canary-mission',
    slug: 'canary-mission',
    name: 'Canary Mission',
    fullName: 'Canary Mission',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2015',
    description: 'Blacklisting website that creates dossiers on students and academics who support Palestinian rights. Profiles include names, photos, social media posts, and university affiliations. Designed to intimidate activists and damage career prospects. Although it operated with no public leadership for years, investigative reporting by The Forward in 2018 revealed that the Jewish Community Federation of San Francisco channelled $100,000 to the operation through a donor-advised fund, and the Jewish Federation of Greater Los Angeles contributed $250,000. Further reporting identified Adam Milstein, a real estate investor and AIPAC board member convicted of tax evasion, as a key funder and promoter of the project.',
    tags: ['Surveillance', 'Campus'],
    relatedOrgs: ['jcf-sf', 'jcf-la', 'act-il'],
    website: 'https://canarymission.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://canarymission.org',
    sources: []
  },
  {
    id: 'uklfi',
    slug: 'uklfi',
    name: 'UK Lawyers for Israel',
    fullName: 'UK Lawyers for Israel (UKLFI)',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    founded: '2011',
    description: 'Pro-Israel legal advocacy group that pressures institutions to remove references to Palestine and suppress pro-Palestinian events through legal threats and complaints. Uses legal intimidation to achieve censorship that direct lobbying cannot.',
    tags: ['Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jnf',
    slug: 'jnf',
    name: 'Jewish National Fund (JNF)',
    fullName: 'Jewish National Fund / Keren Kayemet LeIsrael',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '1901',
    description: 'Plants forests over demolished Palestinian villages, markets settlement land to diaspora buyers, and raises funds for colonisation projects under the guise of environmentalism. Jewish National Fund (Keren Kayemet LeIsrael) forests cover at least 89 destroyed Palestinian villages, physically erasing evidence of the Nakba.',
    tags: ['Settler movement', 'Funding'],
    relatedOrgs: ['wzo', 'jewish-agency'],
    website: 'https://www.jnf.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.jnf.org',
    sources: [
      { name: 'Zochrot: JNF forests over destroyed villages', url: 'https://web.archive.org/web/2024/https://www.zochrot.org/publication_articles/view/55963/en' },
      { name: 'Stop the JNF campaign', url: 'https://web.archive.org/web/2024/https://www.stopthejnf.org/' }
    ]
  },
  {
    id: 'act-il',
    slug: 'act-il',
    name: 'Act.IL',
    fullName: 'Act.IL',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '2017',
    description: 'Israeli government-backed app developed by the Interdisciplinary Center Herzliya in partnership with the Israeli Ministry of Strategic Affairs. Coordinates mass social media campaigns by sending users \'missions\' to like, comment on, or report pro-Palestinian content.',
    tags: ['Surveillance', 'Government'],
    relatedOrgs: [],
    website: 'https://www.act-il.com',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.act-il.com',
    sources: [
      { name: 'DFRLab investigation', url: 'https://medium.com/dfrlab/how-a-political-astroturfing-app-coordinates-pro-israel-influence-operations-bf1104fa5c7f' },
      { name: 'BuzzFeed News exposé', url: 'https://www.buzzfeednews.com/article/ishmaeldaro/act-il-social-media-astroturfing-israel-palestine' }
    ]
  },
  {
    id: 'ihra',
    slug: 'ihra',
    name: 'International Holocaust Remembrance Alliance (IHRA)',
    fullName: 'International Holocaust Remembrance Alliance',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'Intergovernmental',
    founded: '1998',
    description: 'Intergovernmental body whose \'working definition of antisemitism\' includes examples that conflate criticism of Israel with anti-Jewish bigotry. The definition has been adopted by governments and institutions worldwide and weaponised to suppress Palestine solidarity activism, silence academics, and cancel events.',
    tags: ['Government', 'Legal'],
    relatedOrgs: [],
    website: 'https://holocaustremembrance.com/resources/working-definition-antisemitism',
    archivedWebsite: 'https://web.archive.org/web/2024/https://holocaustremembrance.com/resources/working-definition-antisemitism',
    sources: []
  },
  {
    id: 'wujs',
    slug: 'wujs',
    name: 'World Union of Jewish Students (WUJS)',
    fullName: 'World Union of Jewish Students',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '1924',
    description: 'Umbrella organisation for Jewish student unions worldwide. Published the Hasbara Handbook and has coordinated pro-Israel advocacy on university campuses in partnership with the Israeli government since the early 2000s.',
    tags: ['Campus'],
    relatedOrgs: ['hillel'],
    website: '',
    sources: []
  },
  {
    id: 'tip',
    slug: 'the-israel-project',
    name: 'The Israel Project',
    fullName: 'The Israel Project',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2002',
    description: 'US-based media advocacy organisation that commissioned the Luntz Report and distributed talking points to journalists and politicians. Disbanded in 2019 but its materials and strategies continue to circulate widely within the hasbara ecosystem.',
    tags: ['Media', 'Lobbying'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'reut-institute',
    slug: 'reut-institute',
    name: 'Reut Institute',
    fullName: 'The Reut Institute',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'National',
    founded: '2004',
    description: 'Israeli think tank that authored the 2010 strategy paper \'Building a Political Firewall Against Israel\'s Delegitimisation.\' The paper mapped Palestine solidarity networks and proposed a comprehensive counter-strategy that has been widely adopted across the hasbara ecosystem. Coined the framework of categorising critics as a \'delegitimisation network\' to be systematically undermined.',
    tags: ['Think tank'],
    relatedOrgs: [],
    website: '',
    sources: [
      { name: 'Reut Institute delegitimisation report', url: '/library/documents/reut-political-firewall-2010.pdf' }
    ]
  },
  {
    id: 'ministry-strategic-affairs',
    slug: 'ministry-strategic-affairs',
    name: 'Ministry of Strategic Affairs',
    fullName: 'Israeli Ministry of Strategic Affairs and Public Diplomacy',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'National',
    founded: '2006',
    description: 'Israeli government ministry created specifically to combat the BDS movement and Palestine solidarity activism worldwide. Operates covert influence campaigns, funds front organisations, and gathers intelligence on activists in foreign countries. Published \'Behind the Mask\' in 2019 attempting to smear BDS as linked to \'terrorism\'. Merged into the Ministry of Hasbara in 2023.',
    tags: ['Government', 'Surveillance'],
    relatedOrgs: ['act-il', 'standwithus'],
    website: '',
    sources: []
  },
  {
    id: 'jewish-agency',
    slug: 'jewish-agency',
    name: 'Jewish Agency for Israel',
    fullName: 'The Jewish Agency for Israel',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '1929',
    description: 'Established in 1929 as the operative arm of the World Zionist Organisation. Under the British Mandate, coordinated mass Jewish immigration to Palestine, acquired land for Jewish-only settlement, and built the institutional infrastructure that enabled the 1948 Nakba, the ethnic cleansing of over 750,000 Palestinians from their homeland. Today operates as a quasi-governmental body that actively recruits and funds Jewish immigration to the Israeli state, including to illegal settlements in the occupied West Bank, while millions of expelled Palestinians and their descendants are denied the right to return. Runs programmes including Birthright, Masa, and Shlichim (emissaries embedded in Jewish communities worldwide). Partners with national Zionist federations in every major country.',
    tags: ['Government', 'Settler movement'],
    relatedOrgs: ['jnf', 'birthright', 'jfna'],
    website: 'https://www.jewishagency.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.jewishagency.org',
    sources: []
  },
  {
    id: 'adl',
    slug: 'adl',
    name: 'Anti-Defamation League (ADL)',
    fullName: 'Anti-Defamation League',
    country: 'United States',
    countryCode: 'US',
    scope: 'International',
    founded: '1913',
    description: 'Pro-Israel lobbying organisation that operates under the guise of combating antisemitism. Maintains a database tracking Palestine solidarity activists and organisations. Trains US law enforcement in joint programmes with Israeli security forces. Pressures social media platforms to suppress pro-Palestinian content and has labelled Jewish Voice for Peace and Students for Justice in Palestine as organisations of concern. Weaponises accusations of antisemitism to silence opposition to Zionism and shield the Israeli state from accountability.',
    tags: ['Lobbying', 'Surveillance'],
    relatedOrgs: ['aipac'],
    website: 'https://www.adl.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.adl.org',
    sources: []
  },
  {
    id: 'birthright',
    slug: 'birthright',
    name: 'Birthright Israel (Taglit)',
    fullName: 'Taglit-Birthright Israel',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '1999',
    description: 'Mass ideological programme offering free 10-day trips to the Israeli state for young Jewish adults aged 18-26. Co-funded by the Israeli government and private donors including Sheldon Adelson. Has sent over 800,000 participants since its founding. Functions as a settler recruitment pipeline, manufacturing emotional attachment to the land through curated itineraries that erase Palestinian existence, normalise military occupation, and include mandatory socialisation with Israeli soldiers. Designed to funnel participants toward aliyah, Israeli citizenship, and long-term political support for the Israeli state.',
    tags: ['Community', 'Settler movement'],
    relatedOrgs: ['jewish-agency'],
    website: 'https://www.birthrightisrael.com',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.birthrightisrael.com',
    sources: []
  },
  {
    id: 'hillel',
    slug: 'hillel-international',
    name: 'Hillel International',
    fullName: 'Hillel International',
    country: 'United States',
    countryCode: 'US',
    scope: 'International',
    founded: '1923',
    description: 'The largest Jewish campus organisation in the world, operating at over 850 colleges and universities. Hillel\'s Israel Guidelines policy prohibits campus chapters from hosting or partnering with groups that support BDS or \'delegitimise\' the Israeli state, effectively enforcing a pro-Israel political line across hundreds of campuses and censoring any Palestine solidarity activity within Jewish student spaces.',
    tags: ['Campus'],
    relatedOrgs: ['icc', 'standwithus'],
    website: 'https://www.hillel.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.hillel.org',
    sources: []
  },
  {
    id: 'jfna',
    slug: 'jfna',
    name: 'Jewish Federations of North America (JFNA)',
    fullName: 'Jewish Federations of North America',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '1999',
    description: 'Umbrella body for 146 Jewish federations and 300+ communities across North America, collectively raising and distributing over $3 billion annually. A significant portion directly funds programmes in the Israeli state, including settlement infrastructure and pro-Israel advocacy. Local federations function as funding pipelines, channelling tax-deductible donations to Israeli state projects and hasbara organisations. Have been documented funding groups including Canary Mission through donor-advised funds, enabling anonymous financing of blacklisting operations targeting Palestine solidarity activists.',
    tags: ['Funding', 'Community'],
    relatedOrgs: ['jcf-sf', 'jcf-la', 'jewish-agency'],
    website: 'https://www.jewishfederations.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.jewishfederations.org',
    sources: []
  },
  {
    id: 'jcf-sf',
    slug: 'jewish-community-federation-sf',
    name: 'Jewish Community Federation of San Francisco',
    fullName: 'Jewish Community Federation and Endowment Fund of San Francisco, the Peninsula, Marin and Sonoma Counties',
    country: 'United States',
    countryCode: 'US',
    scope: 'Local',
    founded: '1955',
    parentOrg: 'jfna',
    description: 'Regional Jewish federation covering the San Francisco Bay Area. Documented funder of Canary Mission, channelling $100,000 through the Helen Diller Family Foundation, a donor-advised fund it administers. The Forward\'s 2018 investigation identified this as one of the first confirmed financial links to the blacklisting operation.',
    tags: ['Funding'],
    relatedOrgs: ['canary-mission'],
    website: 'https://jewishfed.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://jewishfed.org',
    sources: [
      { name: 'The Forward: Canary Mission secretly bankrolled by major Jewish federation', url: 'https://web.archive.org/web/2024/https://forward.com/news/411355/revealed-canary-mission-blacklist-is-secretly-bankrolled-by-major-jewish/' }
    ]
  },
  {
    id: 'jcf-la',
    slug: 'jewish-community-federation-la',
    name: 'Jewish Federation of Greater Los Angeles',
    fullName: 'Jewish Federation of Greater Los Angeles',
    country: 'United States',
    countryCode: 'US',
    scope: 'Local',
    founded: '1959',
    parentOrg: 'jfna',
    description: 'Regional Jewish federation covering the Greater Los Angeles area. Documented funder of Canary Mission, contributing $250,000 to the blacklisting operation. One of the largest Jewish federations in North America.',
    tags: ['Funding'],
    relatedOrgs: ['canary-mission'],
    website: 'https://www.jewishla.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.jewishla.org',
    sources: [
      { name: 'The Forward: Canary Mission secretly bankrolled by major Jewish federation', url: 'https://web.archive.org/web/2024/https://forward.com/news/411355/revealed-canary-mission-blacklist-is-secretly-bankrolled-by-major-jewish/' }
    ]
  },
  {
    id: 'icc',
    slug: 'israel-on-campus-coalition',
    name: 'Israel on Campus Coalition (ICC)',
    fullName: 'Israel on Campus Coalition',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2002',
    description: 'Coordinates pro-Israel campus strategy across US universities, serving as a clearinghouse connecting Hillel, StandWithUs, AIPAC, and other organisations operating on campuses. Monitors campus events and student government resolutions, deploying rapid-response teams to counter BDS votes and pro-Palestine organising.',
    tags: ['Campus'],
    relatedOrgs: ['hillel', 'standwithus', 'aipac'],
    website: 'https://www.israelcc.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.israelcc.org',
    sources: []
  },
  {
    id: 'aen',
    slug: 'academic-engagement-network',
    name: 'Academic Engagement Network (AEN)',
    fullName: 'Academic Engagement Network',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2015',
    description: 'Network of university faculty who oppose the academic boycott of Israel. Presents itself as a \'moderate\' alternative to more aggressive campus groups while working to prevent academic institutions from adopting BDS resolutions. Funded by pro-Israel foundations.',
    tags: ['Campus'],
    relatedOrgs: ['icc'],
    website: '',
    sources: []
  },
  {
    id: 'ajc',
    slug: 'ajc',
    name: 'American Jewish Committee (AJC)',
    fullName: 'American Jewish Committee',
    country: 'United States',
    countryCode: 'US',
    scope: 'International',
    founded: '1906',
    description: 'One of the oldest pro-Israel lobbying organisations in the US. Operates the Transatlantic Institute in Brussels to lobby European institutions. Played a key role in promoting the IHRA definition of antisemitism internationally and pressuring governments to adopt it.',
    tags: ['Lobbying'],
    relatedOrgs: ['transatlantic-institute', 'ihra'],
    website: 'https://www.ajc.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.ajc.org',
    sources: []
  },
  {
    id: 'transatlantic-institute',
    slug: 'transatlantic-institute',
    name: 'Transatlantic Institute',
    fullName: 'AJC Transatlantic Institute',
    country: 'Belgium',
    countryCode: 'BE',
    scope: 'International',
    founded: '2004',
    parentOrg: 'ajc',
    description: 'The Brussels-based European arm of the American Jewish Committee. Lobbies European Union institutions and member state governments on behalf of Israeli interests. Central to the campaign to embed the IHRA definition into EU policy and legislation.',
    tags: ['Lobbying'],
    relatedOrgs: ['ajc', 'ihra'],
    website: '',
    sources: []
  },
  {
    id: 'wzo',
    slug: 'wzo',
    name: 'World Zionist Organisation (WZO)',
    fullName: 'World Zionist Organisation',
    country: 'Israeli state',
    countryCode: 'IL',
    scope: 'International',
    founded: '1897',
    description: 'Founded by Theodor Herzl, the WZO is the parent body of the Zionist movement. Continues to fund settlement expansion in the occupied West Bank through its Settlement Division and coordinates global Zionist political activity through national federations.',
    tags: ['Settler movement', 'Government'],
    relatedOrgs: ['jewish-agency', 'jnf'],
    website: '',
    sources: []
  },
  {
    id: 'dmfi',
    slug: 'dmfi',
    name: 'Democratic Majority for Israel (DMFI)',
    fullName: 'Democratic Majority for Israel',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2019',
    description: 'Pro-Israel PAC that targets progressive Democratic candidates who support Palestinian rights. Spent millions to defeat candidates like Jamaal Bowman and other members of \'The Squad\' who criticised Israeli military operations. Works to ensure unconditional US support for the Israeli state remains bipartisan.',
    tags: ['Lobbying'],
    relatedOrgs: ['aipac'],
    website: 'https://www.demmajorityforisrael.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.demmajorityforisrael.org',
    sources: []
  },
  {
    id: 'cufi',
    slug: 'christians-united-for-israel',
    name: 'Christians United for Israel (CUFI)',
    fullName: 'Christians United for Israel',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    founded: '2006',
    description: 'The largest pro-Israel organisation in the US by membership, with over 10 million members. A Christian Zionist group founded by Pastor John Hagee that lobbies Congress for unconditional support for the Israeli state. Frames support for Israel in apocalyptic theological terms.',
    tags: ['Lobbying', 'Community'],
    relatedOrgs: ['aipac'],
    website: 'https://www.cufi.org',
    archivedWebsite: 'https://web.archive.org/web/2024/https://www.cufi.org',
    sources: []
  },
  {
    id: 'amcha-initiative',
    slug: 'amcha-initiative',
    name: 'AMCHA Initiative',
    fullName: 'AMCHA Initiative',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Campus monitoring organisation that tracks and documents what it calls antisemitic activity at US universities. Maintains a database of incidents that includes Palestine solidarity events, BDS resolutions, and academic criticism of the Israeli state. Pressures university administrators to suppress pro-Palestine events and discipline faculty members.',
    tags: ['Campus', 'Media', 'Surveillance'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'conference-of-presidents',
    slug: 'conference-of-presidents',
    name: 'Conference of Presidents of Major American Jewish Organisations',
    fullName: 'Conference of Presidents of Major American Jewish Organizations',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Umbrella body representing over 50 national Jewish organisations. Functions as the consensus voice of the American Jewish establishment on matters relating to the Israeli state. Coordinates collective lobbying efforts and provides a unified front in dealings with the US government and foreign governments.',
    tags: ['Lobbying', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'fdd',
    slug: 'fdd',
    name: 'Foundation for Defense of Democracies (FDD)',
    fullName: 'Foundation for Defense of Democracies',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Neoconservative think tank that functions as a key node between the Israeli government and US policy. Advocates for hawkish policies including sanctions, military action, and opposition to diplomacy with Iran. Funded by pro-Israel billionaires including Sheldon Adelson and Bernard Marcus. Staff frequently move between FDD and Israeli government advisory roles.',
    tags: ['Funding', 'Government', 'Think tank'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'friends-of-the-idf',
    slug: 'friends-of-the-idf',
    name: 'Friends of the IDF (FIDF)',
    fullName: 'Friends of the Israel Defense Forces',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'US nonprofit raising funds for Israeli soldiers, including scholarships, welfare, and recreational facilities. Holds an annual gala in New York that is one of the largest single fundraising events in the US nonprofit sector, regularly raising over $60 million in a single evening. Provides direct material support to the military conducting operations in occupied Palestine.',
    tags: ['Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jewish-national-fund-usa',
    slug: 'jewish-national-fund-usa',
    name: 'Jewish National Fund USA (JNF)',
    fullName: 'Jewish National Fund USA',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'The largest national branch of the JNF, raising hundreds of millions of dollars annually for land and development projects in the Israeli state. Funds afforestation over destroyed Palestinian villages, erasing evidence of ethnic cleansing. Finances development in the Naqab region, which the Israeli state rebranded as \'Negev\', part of a systematic campaign to erase indigenous Palestinian and Bedouin ties to the land. Displaces Bedouin communities and builds infrastructure in occupied territories. Despite operating as a registered charity with tax-exempt status, has faced legal challenges in multiple countries over its discriminatory land policies, which restrict land use to Jewish people only. In Canada, its charitable status was revoked in 2017; in the UK and US, campaigners have repeatedly challenged its tax-exempt status on the grounds that it funds discriminatory and exclusionary settlement projects.',
    tags: ['Funding', 'Settler movement', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jinsa',
    slug: 'jinsa',
    name: 'Jewish Institute for National Security of America (JINSA)',
    fullName: 'Jewish Institute for National Security of America',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Think tank promoting the US-Israel military relationship. Organises trips to the Israeli state for retired US generals and admirals, building a network of military voices advocating for Israeli interests. Runs programmes embedding Israeli strategic perspectives into US national security policy.',
    tags: ['Think tank'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'memri',
    slug: 'memri',
    name: 'Middle East Media Research Institute (MEMRI)',
    fullName: 'Middle East Media Research Institute',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Co-founded by Yigal Carmon, a former Israeli military intelligence officer. Selectively translates Arabic, Farsi, and Turkish media for English-speaking audiences, stripping content of context to construct a narrative of endemic Arab and Muslim hostility. Functions as a propaganda laundering operation, feeding decontextualised clips and translations to Western media and politicians who cite MEMRI as a credible source. Has faced longstanding criticism for deliberate selection bias and systematic distortion of source material.',
    tags: ['Lobbying', 'Media'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'republican-jewish-coalition',
    slug: 'republican-jewish-coalition',
    name: 'Republican Jewish Coalition (RJC)',
    fullName: 'Republican Jewish Coalition',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Political advocacy organisation promoting Jewish Republican engagement and unconditional support for the Israeli state within the Republican Party. Major donor network influencing GOP policy positions on the Middle East. Hosts an annual leadership meeting attended by presidential candidates and senior Republican officials.',
    tags: ['Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'simon-wiesenthal-center',
    slug: 'simon-wiesenthal-center',
    name: 'Simon Wiesenthal Center (SWC)',
    fullName: 'Simon Wiesenthal Center',
    country: 'United States',
    countryCode: 'US',
    scope: 'International',
    description: 'Pro-Israel organisation headquartered in Los Angeles that weaponises antisemitism claims to shield the Israeli state from criticism. Publishes an annual \'Global Antisemitism Top Ten\' list that routinely targets Palestine solidarity organisations, BDS campaigns, and critics of Israeli policy, conflating political opposition to a state with bigotry against Jewish people. Operates the Museum of Tolerance in Los Angeles and Jerusalem, using Holocaust memory as a platform for pro-Israel advocacy.',
    tags: ['Lobbying'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'students-supporting-israel',
    slug: 'students-supporting-israel',
    name: 'Students Supporting Israel (SSI)',
    fullName: 'Students Supporting Israel',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Campus advocacy network with over 140 chapters at universities across the US, Canada, and internationally. Founded at the University of Minnesota. Provides a visible pro-Israel student presence, organises counter-demonstrations to Palestine solidarity events, and trains student advocates in hasbara techniques.',
    tags: ['Campus'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'winep',
    slug: 'winep',
    name: 'Washington Institute for Near East Policy (WINEP)',
    fullName: 'Washington Institute for Near East Policy',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'Think tank spun off from AIPAC to provide an academic veneer for pro-Israel policy positions. Produces policy papers and hosts events shaping US Middle East policy. Founded by Martin Indyk, former AIPAC research director who later became US Ambassador to the Israeli state. Staff regularly rotate between WINEP and senior US government positions.',
    tags: ['Campus', 'Lobbying', 'Government', 'Think tank'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'zoa',
    slug: 'zoa',
    name: 'Zionist Organization of America (ZOA)',
    fullName: 'Zionist Organization of America',
    country: 'United States',
    countryCode: 'US',
    scope: 'National',
    description: 'The oldest pro-Israel organisation in the US. Currently presided by Morton Klein, and supports settlement expansion and opposes any recognition of Palestinian rights. Pressures universities to suppress Palestine solidarity events, files complaints against academics who criticise the Israeli state, and lobbies for legislation penalising BDS and criminalising support for Palestinian liberation.',
    tags: ['Campus', 'Lobbying', 'Media', 'Settler movement', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'bicom',
    slug: 'bicom',
    name: 'Britain Israel Communications and Research Centre (BICOM)',
    fullName: 'Britain Israel Communications and Research Centre',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Media-focused lobbying organisation that provides briefings, talking points, and background materials to British journalists, politicians, and opinion formers. Funded by the Poju Zabludowicz Foundation. Operates as the primary communications infrastructure for pro-Israel messaging in UK media, organising journalist trips and providing rapid-response media lines during Israeli military operations.',
    tags: ['Lobbying', 'Media', 'Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'board-of-deputies',
    slug: 'board-of-deputies',
    name: 'Board of Deputies of British Jews',
    fullName: 'Board of Deputies of British Jews',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'The main representative body of British Jews and one of the oldest Jewish communal organisations in the world. Lobbies the UK government, pressures political parties and institutions to adopt the IHRA definition, and has campaigned against pro-Palestine politicians. Played a central role in the campaign against Jeremy Corbyn\'s Labour leadership, coordinating with media to amplify antisemitism allegations.',
    tags: ['Lobbying', 'Media', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'caa',
    slug: 'caa',
    name: 'Campaign Against Antisemitism (CAA)',
    fullName: 'Campaign Against Antisemitism',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Advocacy group that files complaints against individuals and organisations critical of the Israeli state, using antisemitism allegations as a legal and reputational weapon. Has pursued private prosecutions and regulatory complaints against activists, academics, and politicians. Founded during the 2014 Israeli assault on Gaza.',
    tags: ['Campus', 'Lobbying', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'cst',
    slug: 'cst',
    name: 'Community Security Trust (CST)',
    fullName: 'Community Security Trust',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Charity providing security services to the British Jewish community. Publishes annual antisemitism incident reports that include anti-Zionist political expression in their statistics, conflating political criticism with hate crimes. Works closely with UK police and intelligence services. Has been criticised for inflating antisemitism figures by categorising Palestine solidarity activism as antisemitic incidents.',
    tags: ['Media', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'cfi',
    slug: 'cfi',
    name: 'Conservative Friends of Israel (CFI)',
    fullName: 'Conservative Friends of Israel',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Westminster lobbying group with access to over 80% of Conservative MPs. Organises all-expenses-paid trips to the Israeli state for parliamentarians, funds constituency events, and maintains close relationships with party leadership. An Al Jazeera undercover investigation (The Lobby, 2017) documented Israeli embassy involvement in CFI activities and efforts to influence UK politics.',
    tags: ['Lobbying', 'Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jlc',
    slug: 'jlc',
    name: 'Jewish Leadership Council (JLC)',
    fullName: 'Jewish Leadership Council',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Coordinating body bringing together leaders of major British Jewish organisations. Functions as a strategic umbrella connecting the Board of Deputies, CST, UJIA, JNF UK, and other bodies. Lobbies government at the highest levels and coordinates communal responses to political events affecting the Israeli state.',
    tags: ['Lobbying', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jewish-national-fund-uk',
    slug: 'jewish-national-fund-uk',
    name: 'Jewish National Fund UK (JNF)',
    fullName: 'Jewish National Fund UK',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'UK fundraising arm of the Jewish National Fund. Raises funds for afforestation and development projects in the Israeli state, including over Palestinian villages destroyed during the Nakba. Has faced sustained campaigning from the Stop the JNF campaign, which has successfully disrupted fundraising events and raised public awareness of JNF\'s role in colonisation.',
    tags: ['Funding', 'Settler movement'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'lfi',
    slug: 'lfi',
    name: 'Labour Friends of Israel (LFI)',
    fullName: 'Labour Friends of Israel',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Cross-party lobbying group within the Labour Party. Organises delegations to the Israeli state for Labour MPs and coordinates pro-Israel positioning within the party. Played a key role in the internal campaign against Jeremy Corbyn, working alongside the Israeli embassy as documented in Al Jazeera\'s The Lobby investigation.',
    tags: ['Lobbying'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'ujs',
    slug: 'ujs',
    name: 'Union of Jewish Students (UJS)',
    fullName: 'Union of Jewish Students',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Representative body for Jewish students at UK universities. Affiliated with the World Union of Jewish Students (WUSJ). Coordinates campus advocacy, opposes Boycott, Divestment and Sanctions (BDS) motions at student unions, and works with university administrations to adopt the IHRA definition. Has campaigned to deplatform pro-Palestine speakers and defund Palestine solidarity societies.',
    tags: ['Campus', 'Funding', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'ujia',
    slug: 'ujia',
    name: 'United Jewish Israel Appeal (UJIA)',
    fullName: 'United Jewish Israel Appeal',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Major UK Jewish fundraising organisation channelling donations to projects in the Israeli state including education, welfare, and young leadership programmes. Runs the Israel Experience programme sending young British Jews on subsidised trips. Part of the Keren Hayesod global network.',
    tags: ['Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'we-believe-in-israel',
    slug: 'we-believe-in-israel',
    name: 'We Believe in Israel',
    fullName: 'We Believe in Israel',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Grassroots advocacy network that trains and mobilises pro-Israel activists across the UK. Runs an annual conference and regional training events teaching participants how to counter BDS campaigns, respond to media criticism, and lobby local politicians. Closely connected to the Britain Israel Communications and Research Centre (BICOM).',
    tags: ['Lobbying', 'Media'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'zionist-federation-gb',
    slug: 'zionist-federation-gb',
    name: 'Zionist Federation of Great Britain and Ireland',
    fullName: 'Zionist Federation of Great Britain and Ireland',
    country: 'United Kingdom',
    countryCode: 'GB',
    scope: 'National',
    description: 'Umbrella body for Zionist organisations in the UK and Ireland, affiliated with the World Zionist Organization. Coordinates community mobilisation, organises pro-Israel demonstrations, and lobbies government. Runs campaigns promoting immigration to the Israeli state and defending Israeli military operations.',
    tags: ['Lobbying', 'Government', 'Settler movement', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'cjpac',
    slug: 'cjpac',
    name: 'Canadian Jewish Political Affairs Committee (CJPAC)',
    fullName: 'Canadian Jewish Political Affairs Committee',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Non-partisan political engagement organisation that recruits, trains, and supports pro-Israel candidates across all major Canadian political parties. Runs campus fellowship programmes placing students in political offices and organises delegations to the Israeli state for politicians and community leaders. Describes its mission as ensuring \'a strong Canada-Israel relationship.\'',
    tags: ['Campus', 'Lobbying', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'cija',
    slug: 'cija',
    name: 'Centre for Israel and Jewish Affairs (CIJA)',
    fullName: 'Centre for Israel and Jewish Affairs',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'The advocacy arm of the Jewish Federations of Canada, functioning as Canada\'s primary pro-Israel lobbying organisation. Engages federal and provincial politicians, coordinates rapid-response media campaigns, and lobbies for adoption of the International Holocaust Remembrance Alliance (IHRA) working definition of antisemitism across Canadian institutions. Has pushed for legislation targeting the Boycott, Divestment and Sanctions (BDS) movement.',
    tags: ['Lobbying', 'Media', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'fswc',
    slug: 'fswc',
    name: 'Friends of Simon Wiesenthal Center (FSWC)',
    fullName: 'Friends of Simon Wiesenthal Center for Holocaust Studies',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian affiliate of the Simon Wiesenthal Center. Operates the Tour for Humanity mobile education programme and lobbies against BDS. Has pressured Canadian universities and institutions to cancel events featuring pro-Palestine speakers and adopt the IHRA definition. Runs campaigns targeting academics and public figures who criticise the Israeli state.',
    tags: ['Campus', 'Lobbying', 'Media'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'fidf-canada',
    slug: 'fidf-canada',
    name: 'Friends of the IDF Canada (FIDF)',
    fullName: 'Friends of the Israel Defense Forces Canada',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian chapter of Friends of the Israel Defense Forces. Raises funds to support Israeli soldiers through galas and fundraising events across major Canadian cities including Toronto, Montreal, and Vancouver. Provides direct material support to the military conducting operations in occupied Palestine.',
    tags: ['Funding'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'hillel-canada',
    slug: 'hillel-canada',
    name: 'Hillel Canada',
    fullName: 'Hillel Canada (Hillel Ontario / Hillel BC / Hillel Montreal)',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian chapters of Hillel International operating at universities across Ontario, British Columbia, Quebec, and Alberta. Subject to Hillel International\'s Israel Guidelines, which prohibit partnering with groups supporting BDS. Coordinates pro-Israel campus programming and interfaces with university administrations on issues relating to Palestine solidarity organising.',
    tags: ['Campus'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'honest-reporting-canada',
    slug: 'honest-reporting-canada',
    name: 'HonestReporting Canada',
    fullName: 'HonestReporting Canada',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian affiliate of the Israeli-founded media pressure organisation HonestReporting. Monitors Canadian media including the Canadian Broadcasting Corporation (CBC), Globe and Mail, and Toronto Star for coverage it considers biased against the Israeli state. Mobilises mass complaint campaigns targeting journalists and editors, and has claimed credit for corrections and retractions at major outlets.',
    tags: ['Media', 'Surveillance', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jnf-canada',
    slug: 'jnf-canada',
    name: 'Jewish National Fund Canada (JNF)',
    fullName: 'Jewish National Fund of Canada',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian fundraising arm of the Jewish National Fund. Raises millions annually for afforestation and development projects in the Israeli state, including over destroyed Palestinian villages. Has faced sustained protests and calls to revoke its charitable tax status due to its role in colonisation and discriminatory land policies. In 2017, its charitable status was challenged in a complaint to the Canada Revenue Agency.',
    tags: ['Funding', 'Settler movement', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'standwithus-canada',
    slug: 'standwithus-canada',
    name: 'StandWithUs Canada',
    fullName: 'StandWithUs Canada',
    country: 'Canada',
    countryCode: 'CA',
    scope: 'National',
    description: 'Canadian branch of the US-based pro-Israel campus advocacy organisation. Provides training materials, campus toolkits, and coordinates student advocates at Canadian universities. Part of the global StandWithUs network partially funded by the Israeli government\'s Ministry of Strategic Affairs.',
    tags: ['Campus', 'Funding', 'Government'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'amadeu-antonio',
    slug: 'amadeu-antonio',
    name: 'Amadeu Antonio Foundation',
    fullName: 'Amadeu Antonio Stiftung',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'A self-proclaimed anti-racism foundation focused on combating what it defines as \'Israel-related antisemitism.\' Advises the German government on antisemitism policy and has been criticised for conflating Palestine solidarity with antisemitism in its reports and training materials. Plays a significant role in shaping institutional responses to pro-Palestinian expression in Germany.',
    tags: ['Media', 'Government'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'zentralrat',
    slug: 'zentralrat',
    name: 'Central Council of Jews in Germany (Zentralrat)',
    fullName: 'Zentralrat der Juden in Deutschland',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'The official representative body of the Jewish community in Germany. Holds enormous political influence due to Germany\'s historical responsibility for the Holocaust. Has consistently equated criticism of the Israeli state with antisemitism and pressured the German government to suppress Palestine solidarity activism, cancel cultural events, and adopt the IHRA definition as a binding standard for public funding decisions.',
    tags: ['Media', 'Funding', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'antisemitism-commissioner-de',
    slug: 'antisemitism-commissioner-de',
    name: 'Federal Commissioner for Antisemitism (Germany)',
    fullName: 'Beauftragter der Bundesregierung für jüdisches Leben in Deutschland und den Kampf gegen Antisemitismus',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'Government office established in 2018, currently held by Felix Klein. Has called for banning pro-Palestine demonstrations, supported the Bundestag\'s anti-BDS resolution, and advocated for tying public funding to acceptance of the IHRA definition. Under Klein\'s leadership, the office has been a driving force behind Germany\'s aggressive suppression of Palestine solidarity, including cancelling the residency permits of Palestinian cultural figures and pressuring cultural institutions to disinvite critics of the Israeli state.',
    tags: ['Media', 'Funding', 'Government'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'dig',
    slug: 'dig',
    name: 'German-Israeli Society (DIG)',
    fullName: 'Deutsch-Israelische Gesellschaft',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'German-Israeli friendship organisation with over 50 regional chapters. Organises cultural events, political discussions, and delegations to the Israeli state. Lobbies German politicians to maintain unconditional support for the Israeli state and has campaigned against BDS, pro-Palestine demonstrations, and academic boycott initiatives at German universities.',
    tags: ['Campus', 'Lobbying'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jnf-germany',
    slug: 'jnf-germany',
    name: 'Jewish National Fund Germany (JNF-KKL)',
    fullName: 'Jüdischer Nationalfonds - Keren Kayemeth LeIsrael Deutschland',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'German branch of the Jewish National Fund. Raises funds for afforestation and development projects in the Israeli state. Operates within the broader German institutional framework that treats support for the Israeli state as a matter of \'Staatsräson\' (state doctrine).',
    tags: ['Funding', 'Settler movement'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'wjc-germany',
    slug: 'wjc-germany',
    name: 'World Jewish Congress German Section',
    fullName: 'World Jewish Congress - German Section',
    country: 'Germany',
    countryCode: 'DE',
    scope: 'National',
    description: 'German section of the World Jewish Congress. Works closely with the Zentralrat and the German government on antisemitism policy. Has lobbied for the Bundestag\'s 2019 anti-BDS resolution and pressured German cultural institutions to withdraw invitations to artists and intellectuals critical of the Israeli state.',
    tags: ['Lobbying', 'Media', 'Government'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'aujs',
    slug: 'aujs',
    name: 'Australasian Union of Jewish Students (AUJS)',
    fullName: 'Australasian Union of Jewish Students',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Peak body for Jewish students across Australia and New Zealand. Coordinates pro-Israel campus activities, organises delegations to the Israeli state, and interfaces with university administrations on issues including countering BDS resolutions and pro-Palestine student organising. Works closely with the Zionist Federation of Australia (ZFA) and international campus advocacy networks.',
    tags: ['Campus', 'Settler movement'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'azyc',
    slug: 'azyc',
    name: 'Australasian Zionist Youth Council (AZYC)',
    fullName: 'Australasian Zionist Youth Council',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Umbrella body coordinating Zionist youth movements across Australia and New Zealand, including Betar, Bnei Akiva, Habonim Dror, Hashomer Hatzair, Hineni, and Netzer. Provides a pipeline from youth education (ages 8-18) into adult Zionist organisations and campus groups. Coordinates the Shnat gap year programme sending school leavers to the Israeli state.',
    tags: ['Campus', 'Settler movement', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'aicc',
    slug: 'aicc',
    name: 'Australia-Israel Chamber of Commerce (AICC)',
    fullName: 'Australia-Israel Chamber of Commerce',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Business networking organisation with offices in Sydney, Melbourne, Adelaide, Brisbane, Perth, and Tel Aviv. Over 1,000 member companies. Organises trade missions to the Israeli state and high-profile business events that normalise economic ties. Facilitates trade and investment relationships between Australian businesses and those in the Israeli state.',
    tags: ['Lobbying'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'aijac',
    slug: 'aijac',
    name: 'Australia/Israel & Jewish Affairs Council (AIJAC)',
    fullName: 'Australia/Israel & Jewish Affairs Council',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australia\'s most influential pro-Israel think tank and media watchdog. Publishes the Australia/Israel Review and operates the Rambam Israel Fellowship, an all-expenses-paid programme that sends Australian politicians, journalists, academics, and student leaders on sponsored trips to the Israeli state. Alumni include former prime ministers Scott Morrison, Tony Abbott, Malcolm Turnbull, and Julia Gillard, as well as current opposition leader Peter Dutton. Described in a 2021 study as one of Australia\'s \'best-funded foreign influence operations.\' Between 2018 and 2022, Australian parliamentarians received more sponsored visits to the Israeli state than to any other country except Taiwan and the US.',
    tags: ['Campus', 'Lobbying', 'Media', 'Funding', 'Think tank'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'five-a',
    slug: 'five-a',
    name: 'Australian Academic Alliance Against Antisemitism (5A)',
    fullName: 'Australian Academic Alliance Against Antisemitism',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Coalition of 290 academics and professional staff from 29 Australian universities formed in November 2023. Advocates for adoption of the IHRA working definition of antisemitism at universities. In February 2025, Universities Australia adopted definitions supported by 5A, which the Australia Palestine Advocacy Network criticised as a "politically motivated manoeuvre" to suppress legitimate criticism of the Israeli state on campuses.',
    tags: ['Campus', 'Media'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'aja',
    slug: 'aja',
    name: 'Australian Jewish Association (AJA)',
    fullName: 'Australian Jewish Association',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Social media-focused advocacy organisation that runs aggressive online campaigns and has been associated with far-right political figures and anti-Muslim rhetoric alongside its pro-Israel advocacy.',
    tags: ['Media', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'c4i-australia',
    slug: 'c4i-australia',
    name: 'Christians for Israel Australia (C4I)',
    fullName: 'Christians for Israel Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australian branch of the international Christian Zionist organisation. Non-denominational movement uniting Christians in support of the Israeli state, framed as repentance for historical Jewish suffering. Part of a global network that provides a significant base of non-Jewish political support for Zionist causes.',
    tags: ['Religious'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'ecaj',
    slug: 'ecaj',
    name: 'Executive Council of Australian Jewry (ECAJ)',
    fullName: 'Executive Council of Australian Jewry',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Peak representative body of the Australian Jewish community, serving as the political interface with the federal government. Constituent members include the NSW Jewish Board of Deputies, Jewish Community Council of Victoria, and state boards across Queensland, Western Australia, and other states. Lobbies on issues including adoption of the IHRA definition of antisemitism, countering BDS, and maintaining bipartisan support for the Israeli state in parliament.',
    tags: ['Lobbying', 'Government', 'Settler movement', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'honest-reporting-australia',
    slug: 'honest-reporting-australia',
    name: 'HonestReporting Australia',
    fullName: 'HonestReporting Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australian affiliate of the Israeli-founded media pressure organisation HonestReporting. Monitors Australian media for coverage it considers biased against the Israeli state and mobilises complaint campaigns targeting journalists and outlets. A 2025 report by the Institute for Palestine Studies documented how HonestReporting globally has been "shaping Gaza war coverage and endangering journalists."',
    tags: ['Media', 'Surveillance', 'Legal'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jccv',
    slug: 'jccv',
    name: 'Jewish Community Council of Victoria (JCCV)',
    fullName: 'Jewish Community Council of Victoria',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'Local',
    description: 'Peak representative body of the Jewish community in Victoria, with 54 affiliated organisations. Successor to the Victorian Jewish Board of Deputies (established 1948) and the Melbourne Jewish Advisory Board (1921). Constituent of the ECAJ.',
    tags: ['Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'jnf-australia',
    slug: 'jnf-australia',
    name: 'Jewish National Fund Australia (JNF)',
    fullName: 'Jewish National Fund of Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australian fundraising arm of the Jewish National Fund (Keren Kayemet LeIsrael). Raises funds from Australian donors for land and development projects in the Israeli state, including afforestation programmes that have planted forests over demolished Palestinian villages. Operates state branches across Australia and runs the annual JNF Gala and Blue Box appeals.',
    tags: ['Funding', 'Settler movement'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'maccabi-australia',
    slug: 'maccabi-australia',
    name: 'Maccabi Australia',
    fullName: 'Maccabi Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Jewish Australian sporting organisation affiliated with the Zionist Federation of Australia and part of the global Maccabi World Union. Originally established as the Australian Judean Sports Council in 1935. Organises teams across football, basketball, table tennis, and other sports. Connects Jewish identity with Zionism through sporting and cultural events.',
    tags: ['Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'nain',
    slug: 'nain',
    name: 'Never Again Is Now (NAIN)',
    fullName: 'Never Again Is Now',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Christian Zionist grassroots movement founded in October 2023, led by Reverend Mark Leach. Mobilises Christian and secular communities to stand against antisemitism while promoting pro-Israel advocacy. Organised major rallies across Australian capital cities, with a Sydney rally drawing approximately 10,000 participants in February 2024. Embraced by mainstream Jewish organisations including ECAJ, Zionism Victoria, and JCCV.',
    tags: ['Religious'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'nsw-jbd',
    slug: 'nsw-jbd',
    name: 'NSW Jewish Board of Deputies (JBD)',
    fullName: 'NSW Jewish Board of Deputies',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'Local',
    description: 'Representative body of the Jewish community in New South Wales, Australia\'s most populous state. Constituent of the ECAJ. Engages with state government, media, and institutions on issues including advocacy for the Israeli state, countering pro-Palestine activism, and promoting adoption of the IHRA definition.',
    tags: ['Media', 'Government', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'standwithus-australia',
    slug: 'standwithus-australia',
    name: 'StandWithUs Australia',
    fullName: 'StandWithUs Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australian branch of the US-based pro-Israel campus advocacy organisation. Provides training materials, campus toolkits, and coordinates student advocates at Australian universities. Part of a global network partially funded by the Israeli government\'s Ministry of Strategic Affairs.',
    tags: ['Campus', 'Funding', 'Government'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'ssi-australia',
    slug: 'ssi-australia',
    name: 'Students Supporting Israel Australia (SSI)',
    fullName: 'Students Supporting Israel - Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Australian chapters of the international pro-Israel campus network, which has over 140 chapters globally. Coordinates campus advocacy activities and provides a visible pro-Israel presence at Australian universities alongside AUJS.',
    tags: ['Campus'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'uia-australia',
    slug: 'uia-australia',
    name: 'United Israel Appeal Australia (UIA)',
    fullName: 'United Israel Appeal Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Major fundraising organisation channelling Australian donations to projects in the Israeli state. Funds settlement-adjacent welfare programmes, immigrant absorption, and youth at risk programmes. Part of the global Keren Hayesod network. One of the largest Zionist philanthropic operations in Australia.',
    tags: ['Funding', 'Settler movement'],
    relatedOrgs: [],
    website: '',
    sources: []
  },
  {
    id: 'zfa',
    slug: 'zfa',
    name: 'Zionist Federation of Australia (ZFA)',
    fullName: 'Zionist Federation of Australia',
    country: 'Australia',
    countryCode: 'AU',
    scope: 'National',
    description: 'Peak Zionist body in Australia, coordinating state-level Zionist councils across NSW, Victoria, Queensland, South Australia, Western Australia, and the ACT. Represents the World Zionist Organisation in Australia and serves as the political umbrella for dozens of affiliated Zionist organisations. Lobbies federal and state governments, coordinates community mobilisation campaigns, and organises delegations to the Israeli state.',
    tags: ['Lobbying', 'Government', 'Settler movement', 'Community'],
    relatedOrgs: [],
    website: '',
    sources: []
  }
];

// ===== MATERIALS =====
export const materialsData = [
  {
    id: 'hasbara-handbook',
    slug: 'hasbara-handbook',
    title: 'Hasbara handbook: promoting Israel on campus',
    description: 'Published by the World Union of Jewish Students (WUJS), this manual teaches pro-Israel activists how to reframe debates, use emotional arguments, and control narratives about Palestine on university campuses. Includes specific rhetorical techniques like \'point scoring\' and \'deflection\', provides templates for responding to common criticisms of Israel, and instructs advocates on how to dominate campus discourse through organised campaigns.',
    tags: ['Training materials', 'Student activism'],
    tactics: ['Deflection', 'Emotional manipulation'],
    type: 'pdf',
    orgId: 'wujs',
    date: '2002',
    hostedUrl: '/library/documents/hasbara-handbook-2002.pdf',
    pages: 68,
    sources: [
      { name: 'Full PDF (Hasbara Tracker archive)', url: '/library/documents/hasbara-handbook-2002.pdf' },
      { name: 'Electronic Intifada analysis', url: 'https://electronicintifada.net/content/inside-israels-million-dollar-troll-army/27566' }
    ]
  },
  {
    id: 'luntz-report',
    slug: 'luntz-report',
    title: 'The Luntz report: global language dictionary',
    description: 'A confidential 116-page document written by Republican pollster Frank Luntz for The Israel Project. Provides tested talking points and word choices designed to make Israeli policies more palatable to American audiences. Instructs spokespeople to say \'neighbourhoods\' instead of \'settlements\' and to always start with empathy before pivoting to Israeli security narratives.',
    tags: ['Talking points', 'Media guides'],
    tactics: ['Emotional manipulation', 'Appeal to security'],
    type: 'pdf',
    orgId: 'tip',
    date: '2009',
    hostedUrl: '/library/documents/luntz-report-2009.pdf',
    pages: 116,
    sources: [
      { name: 'Full PDF (Hasbara Tracker archive)', url: '/library/documents/luntz-report-2009.pdf' },
      { name: 'Internet Archive copy', url: 'https://archive.org/details/TheIsraelProjectTheGlobalLanguageDictionary2009HasbaraManual' }
    ]
  },
  {
    id: 'reut-firewall',
    slug: 'reut-political-firewall',
    title: 'Building a Political Firewall Against Israel\'s Delegitimisation',
    description: 'A 2010 strategy paper by the Reut Institute outlining a framework for countering what it terms the "delegitimisation network" targeting Israel. Proposes a comprehensive counter-strategy including mapping activist networks, engaging with "soft critics," and building alliances with civil society organisations. The document has been widely cited as a foundational text in the organised pushback against Palestine solidarity movements.',
    tags: ['Lobby & advocacy'],
    tactics: ['Manufactured consent', 'Deflection'],
    type: 'pdf',
    orgId: 'reut-institute',
    date: '2010',
    hostedUrl: '/library/documents/reut-political-firewall-2010.pdf',
    pages: 92,
    sources: [
      { name: 'Full PDF (Hasbara Tracker archive)', url: '/library/documents/reut-political-firewall-2010.pdf' }
    ]
  },
  {
    id: 'behind-the-mask',
    slug: 'behind-the-mask',
    title: 'Behind the Mask: The Antisemitic Nature of BDS Exposed',
    description: 'Published by the Israeli Ministry of Strategic Affairs in 2019, this report attempts to link the BDS movement to organisations designated as terrorist groups. Uses guilt-by-association techniques, connecting individuals who have attended the same conferences or appeared on the same panels. Widely criticised by human rights groups as a smear campaign designed to criminalise legitimate political advocacy.',
    tags: ['Government programmes'],
    tactics: ['Disinformation', 'Censorship & suppression'],
    type: 'pdf',
    orgId: 'ministry-strategic-affairs',
    date: '2019',
    hostedUrl: '/library/documents/ministry-strategic-affairs-behind-the-mask-2019.pdf',
    pages: 40,
    sources: [
      { name: 'Full PDF (Hasbara Tracker archive)', url: '/library/documents/ministry-strategic-affairs-behind-the-mask-2019.pdf' },
      { name: 'The Intercept analysis', url: 'https://theintercept.com/2019/02/12/israel-bds-anti-bds-campaign/' }
    ]
  }
];

// ===== INCIDENTS =====
export const incidentsData = [
  {
    id: 'british-museum-palestine-2026',
    slug: 'british-museum-removes-palestine',
    title: 'British Museum removes \'Palestine\' from ancient history displays',
    date: '2026-02-15',
    country: 'United Kingdom',
    countryCode: 'GB',
    institution: 'British Museum',
    institutionType: 'Museum',
    description: 'The British Museum removed the term \'Palestine\' from informational panels and maps in its Middle East galleries, replacing references to ancient \'Palestine\' with \'Canaan\'. The changes affect displays covering 1700-1500 BC that had previously used the historically established geographical term. Labels describing the Hyksos people as being of \'Palestinian descent\' were changed to \'Canaanite descent\'.',
    pressuredBy: ['uklfi'],
    pressureDetail: 'UK Lawyers for Israel (UKLFI) wrote to museum director Nicholas Cullinan claiming the term \'retroactively applies\' to periods where \'no such entity existed\' and \'risks obscuring the history of Israel and the Jewish people\'.',
    currentState: 'Developing',
    tags: ['Cultural erasure', 'Institutional capture'],
    response: 'Museum claimed changes were part of a year-long review unrelated to UKLFI. Director called media coverage a \'misrepresentation\'. Change.org petition launched with 50,000+ signatures. Scholars condemned the move as part of systematic erasure of Palestinian history.',
    updates: [
      { date: '2026-02-15', text: 'Reports confirmed the British Museum changed Middle East gallery labels', source: 'https://web.archive.org/web/2024/https://www.middleeasteye.net/news/british-museum-removal-term-palestine-displays-part-systematic-attempt-erase-it-history' },
      { date: '2026-02-15', text: 'UKLFI confirmed it had written to museum director about the term', source: 'https://web.archive.org/web/2024/https://www.middleeastmonitor.com/20260215-british-museum-removes-the-word-palestine-from-displays-following-pressure-by-pro-israel-lawyers/' },
      { date: '2026-02-16', text: 'Change.org petition launched calling for reinstatement', source: 'https://www.change.org/p/erasing-a-word-erases-a-people-reinstate-palestine-in-the-british-museum' },
      { date: '2026-02-17', text: 'Museum director called media reports a \'misrepresentation\'', source: 'https://web.archive.org/web/2024/https://www.museumsassociation.org/museums-journal/news/2026/02/row-after-british-museum-removes-term-palestine-from-some-displays/' }
    ],
    sources: [
      { name: 'Middle East Eye report', url: 'https://web.archive.org/web/2024/https://www.middleeasteye.net/news/british-museum-removal-term-palestine-displays-part-systematic-attempt-erase-it-history' },
      { name: 'Museums Association coverage', url: 'https://web.archive.org/web/2024/https://www.museumsassociation.org/museums-journal/news/2026/02/row-after-british-museum-removes-term-palestine-from-some-displays/' },
      { name: 'Middle East Monitor: pro-Israel lawyers pressure', url: 'https://web.archive.org/web/2024/https://www.middleeastmonitor.com/20260215-british-museum-removes-the-word-palestine-from-displays-following-pressure-by-pro-israel-lawyers/' },
      { name: 'Change.org petition', url: 'https://www.change.org/p/erasing-a-word-erases-a-people-reinstate-palestine-in-the-british-museum' }
    ],
    relatedMaterials: ['ihra-definition'],
    relatedOrgs: ['uklfi', 'ihra']
  },
  {
    id: 'columbia-sjp-suspension-2023',
    slug: 'columbia-sjp-suspension',
    title: 'Columbia University suspends Palestine solidarity groups',
    date: '2023-11-10',
    country: 'United States',
    countryCode: 'US',
    institution: 'Columbia University',
    institutionType: 'University',
    description: 'Columbia University suspended its Students for Justice in Palestine (SJP) and Jewish Voice for Peace (JVP) chapters following pressure from donors and political figures. The decision preceded the broader campus encampment movement and set a precedent for suppressing Palestine solidarity organising at US universities.',
    pressuredBy: [],
    pressureDetail: 'Major donors, political figures, and media campaigns targeting the university administration.',
    currentState: 'Currently in effect',
    tags: ['Academic suppression', 'Censorship'],
    response: 'Students challenged the suspensions through legal and administrative channels. The decision galvanised the broader campus Palestine solidarity movement in 2024.',
    updates: [
      { date: '2023-11-10', text: 'Columbia suspended SJP and JVP chapters', source: 'https://web.archive.org/web/2024/https://www.columbiaspectator.com/news/2023/11/10/columbia-suspends-sjp-and-jvp-following-unauthorized-thursday-walkout/' },
      { date: '2023-11-15', text: 'Hundreds of students and faculty protested the suspensions', source: 'https://web.archive.org/web/2024/https://www.columbiaspectator.com/news/2023/11/15/to-shut-us-down-you-have-only-made-us-stronger-hundreds-of-students-faculty-protest-suspension-of-sjp-jvp/' },
      { date: '2024-04-17', text: 'Students established protest encampment on campus' }
    ],
    sources: [
      { name: 'Columbia Spectator: Columbia suspends SJP and JVP', url: 'https://web.archive.org/web/2024/https://www.columbiaspectator.com/news/2023/11/10/columbia-suspends-sjp-and-jvp-following-unauthorized-thursday-walkout/' },
      { name: 'Inside Higher Ed: Columbia suspends two pro-Palestinian groups', url: 'https://web.archive.org/web/2024/https://www.insidehighered.com/news/students/free-speech/2023/11/10/columbia-suspends-two-pro-palestinian-groups' }
    ],
    relatedMaterials: [],
    relatedOrgs: []
  },
  {
    id: 'germany-river-sea-ban-2023',
    slug: 'germany-river-to-sea-ban',
    title: 'Germany criminalises \'from the river to the sea\' chant',
    date: '2023-11-02',
    country: 'Germany',
    countryCode: 'DE',
    institution: 'German Interior Ministry',
    institutionType: 'Government',
    description: 'German interior minister Nancy Faeser classified the phrase \'From the River to the Sea\' as a Hamas symbol, making its use prosecutable under Section 86a (symbols of terrorist organisations) with penalties of up to three years\' imprisonment. Police in Berlin alone prosecuted over 5,000 \'anti-Israeli\' offences in the first year after October 2023. German courts have since issued conflicting rulings, with some convicting and others acquitting on free expression grounds.',
    pressuredBy: ['ihra'],
    pressureDetail: 'Germany\'s adoption of the IHRA definition and broader policy of equating pro-Palestine expression with antisemitism.',
    currentState: 'Currently in effect',
    tags: ['Legislative', 'Censorship'],
    response: 'A court in Mannheim ruled the phrase is protected by constitutional free expression. The Duisburg Regional Court acquitted a defendant in May 2025. The Federal Court of Justice (Bundesgerichtshof) is expected to issue a definitive ruling.',
    updates: [
      { date: '2023-11-02', text: 'German interior minister classified the phrase as a Hamas symbol', source: 'https://web.archive.org/web/2024/https://www.wsws.org/en/articles/2023/11/20/zvsr-n20.html' },
      { date: '2023-11-09', text: 'First arrests of demonstrators for using the phrase', source: 'https://web.archive.org/web/2024/https://www.aljazeera.com/news/2023/11/10/germany-gaza-protests-crackdown' },
      { date: '2024-03-22', text: 'Administrative Court of Hesse ruled the phrase could be used at demonstrations' },
      { date: '2025-05-26', text: 'Duisburg Regional Court acquitted defendant, ruling the chant is not punishable', source: 'https://web.archive.org/web/2024/https://www.wsws.org/en/articles/2025/05/26/hqzu-m26.html' }
    ],
    sources: [
      { name: 'Al Jazeera: Germany Gaza protests crackdown', url: 'https://web.archive.org/web/2024/https://www.aljazeera.com/news/2023/11/10/germany-gaza-protests-crackdown' },
      { name: 'WSWS: Germany to prosecute slogan as a crime', url: 'https://web.archive.org/web/2024/https://www.wsws.org/en/articles/2023/11/20/zvsr-n20.html' },
      { name: 'The Left Berlin: How Germany tries to ban the slogan', url: 'https://web.archive.org/web/2024/https://theleftberlin.com/how-germany-tries-to-ban-the-slogan-from-the-river-to-the-sea/' }
    ],
    relatedMaterials: ['ihra-definition'],
    relatedOrgs: ['ihra']
  },
  {
    id: 'nyt-salem-firing-2022',
    slug: 'nyt-salem-firing',
    title: 'New York Times fires Gaza freelancer Hosam Salem over social media posts',
    date: '2022-01-08',
    country: 'United States',
    countryCode: 'US',
    institution: 'The New York Times',
    institutionType: 'Media',
    description: 'The NYT terminated its relationship with Gaza-based freelance photographer Hosam Salem after HonestReporting flagged decade-old social media posts. Salem said a Dutch editor who had obtained Israeli citizenship wrote the report that led to his firing. He said HonestReporting then actively discouraged other international agencies from working with him and his colleagues.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'HonestReporting published articles flagging Salem\'s social media history, prompting an internal review at the Times. Salem identified a Dutch editor with Israeli citizenship as the author of the report.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'Press freedom organisations criticised the termination as disproportionate and part of a pattern of silencing Palestinian voices in Western media.',
    updates: [
      { date: '2022-01-08', text: 'NYT confirmed termination of freelance relationship with Hosam Salem', source: 'https://web.archive.org/web/2024/https://www.middleeastmonitor.com/20221006-new-york-times-sacks-gaza-journalist-for-expressing-support-for-palestinian-resistance/' }
    ],
    sources: [
      { name: 'Middle East Monitor: NYT sacks Gaza journalist', url: 'https://web.archive.org/web/2024/https://www.middleeastmonitor.com/20221006-new-york-times-sacks-gaza-journalist-for-expressing-support-for-palestinian-resistance/' },
      { name: 'Mondoweiss: Palestinian photog says NYT fired him', url: 'https://web.archive.org/web/2024/https://mondoweiss.net/2022/10/palestinian-photog-says-nyt-fired-him-for-expressing-support-for-resistance/' },
      { name: 'ScheerPost: Meet the Israel-linked pressure group getting Palestinian journalists fired', url: 'https://web.archive.org/web/2024/https://scheerpost.com/2022/11/11/honest-reporting-meet-the-israel-linked-pressure-group-getting-palestinian-journalists-fired/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'nyt-hanona-firing-2022',
    slug: 'nyt-hanona-firing',
    title: 'New York Times cuts ties with second Gaza freelancer Fady Hanona',
    date: '2022-01-10',
    country: 'United States',
    countryCode: 'US',
    institution: 'The New York Times',
    institutionType: 'Media',
    description: 'Days after the Salem termination, the NYT severed ties with a second Gaza freelance producer and fixer, Fady Hanona, following the same HonestReporting campaign targeting Palestinian journalists working for Western outlets. Hanona had been credited by the paper in multiple stories.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'Part of HonestReporting\'s campaign scrutinising the social media histories of Palestinian freelancers at major Western outlets.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'The Committee to Protect Journalists expressed concern about the chilling effect on Palestinian journalists.',
    updates: [
      { date: '2022-01-10', text: 'NYT confirmed ending relationship with second Gaza freelancer, Fady Hanona', source: 'https://web.archive.org/web/2024/https://www.commondreams.org/news/2022/10/05/freelance-photographer-says-he-was-fired-nyt-over-support-palestinian-resistance' }
    ],
    sources: [
      { name: 'Common Dreams: Freelance photographer says he was fired by NYT', url: 'https://web.archive.org/web/2024/https://www.commondreams.org/news/2022/10/05/freelance-photographer-says-he-was-fired-nyt-over-support-palestinian-resistance' },
      { name: 'ScheerPost: Meet the Israel-linked pressure group getting Palestinian journalists fired', url: 'https://web.archive.org/web/2024/https://scheerpost.com/2022/11/11/honest-reporting-meet-the-israel-linked-pressure-group-getting-palestinian-journalists-fired/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'nyt-hijjy-firing-2022',
    slug: 'nyt-hijjy-firing',
    title: 'New York Times terminates third Gaza freelancer Soliman Hijjy',
    date: '2022-01-12',
    country: 'United States',
    countryCode: 'US',
    institution: 'The New York Times',
    institutionType: 'Media',
    description: 'The NYT terminated its relationship with a third Gaza-based freelancer, filmmaker Soliman Hijjy, within the same week. HonestReporting subsequently claimed credit for all three dismissals. The systematic targeting of three Palestinian freelancers at a single outlet demonstrated the effectiveness of coordinated media pressure campaigns.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'Third termination resulting from HonestReporting\'s campaign. The organisation published a video claiming credit for all three dismissals.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'Palestinian journalists\' associations condemned the mass terminations as collective punishment. Salem warned that the campaign was designed to silence Palestinian voices and ensure only the Israeli narrative is conveyed.',
    updates: [
      { date: '2022-01-12', text: 'NYT confirmed ending relationship with third Gaza freelancer, Soliman Hijjy', source: 'https://web.archive.org/web/2024/https://scheerpost.com/2022/11/11/honest-reporting-meet-the-israel-linked-pressure-group-getting-palestinian-journalists-fired/' }
    ],
    sources: [
      { name: 'ScheerPost: Meet the Israel-linked pressure group getting Palestinian journalists fired', url: 'https://web.archive.org/web/2024/https://scheerpost.com/2022/11/11/honest-reporting-meet-the-israel-linked-pressure-group-getting-palestinian-journalists-fired/' },
      { name: 'Common Dreams: Freelance photographer says he was fired by NYT', url: 'https://web.archive.org/web/2024/https://www.commondreams.org/news/2022/10/05/freelance-photographer-says-he-was-fired-nyt-over-support-palestinian-resistance' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'reuters-freelancer-firing-2022',
    slug: 'reuters-freelancer-firing',
    title: 'Reuters terminates Gaza freelancer following HonestReporting campaign',
    date: '2022-02-04',
    country: 'United Kingdom',
    countryCode: 'GB',
    institution: 'Reuters',
    institutionType: 'Media',
    description: 'Reuters terminated its relationship with a Gaza-based freelance photographer after HonestReporting extended its campaign beyond the New York Times to target Palestinian freelancers at wire services. The pattern of systematically targeting Palestinian journalists at Western outlets continued across multiple news agencies.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'HonestReporting extended its campaign beyond the NYT to other wire services employing Palestinian freelancers in Gaza.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'Press freedom groups noted the systematic nature of the campaign across multiple outlets.',
    updates: [
      { date: '2022-02-04', text: 'Reuters confirmed termination of freelance relationship following media pressure campaign' }
    ],
    sources: [
      { name: 'ScheerPost: Meet the Israel-linked pressure group getting Palestinian journalists fired', url: 'https://web.archive.org/web/2024/https://scheerpost.com/2022/11/11/honest-reporting-meet-the-israel-linked-pressure-group-getting-palestinian-journalists-fired/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'deutsche-welle-palestinian-journalists-2022',
    slug: 'deutsche-welle-palestinian-journalists',
    title: 'Deutsche Welle fires Palestinian journalists, courts rule dismissals unlawful',
    date: '2022-02-07',
    country: 'Germany',
    countryCode: 'DE',
    institution: 'Deutsche Welle',
    institutionType: 'Media',
    description: 'German state broadcaster Deutsche Welle fired at least seven Arab and Palestinian journalists, including Farah Maraqa and Maram Salem, after a Suddeutsche Zeitung article flagged their social media posts. DW commissioned an investigation led by former justice minister Sabine Leutheusser-Schnarrenberger and Ahmad Mansour, a Palestinian-German psychologist known for pro-Israeli views, which used the IHRA definition to assess the posts. German labour courts subsequently ruled multiple dismissals were unlawful and ordered reinstatement.',
    pressuredBy: ['ihra'],
    pressureDetail: 'DW\'s investigation was based on the IHRA definition of antisemitism and led by figures with known pro-Israeli positions. The European Legal Support Centre said the dismissals manifested anti-Palestinian sentiment.',
    currentState: 'Overturned',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'The Bonn labour court ruled Maram Salem\'s dismissal invalid in July 2022, finding her posts were not antisemitic. The Berlin labour court ruled Farah Maraqa\'s dismissal legally unjustified in September 2022, finding DW had misinformed its staff council. DW\'s appeal was also rejected in June 2023.',
    updates: [
      { date: '2022-02-07', text: 'Deutsche Welle fired seven Arab and Palestinian journalists', source: 'https://web.archive.org/web/2024/https://www.aljazeera.com/news/2022/2/11/career-assassination-dws-scapegoating-of-arab-employees' },
      { date: '2022-07-01', text: 'Bonn labour court ruled Maram Salem\'s dismissal unlawful', source: 'https://web.archive.org/web/2024/https://electronicintifada.net/blogs/ali-abunimah/deutsche-welle-illegally-fired-palestinian-journalist-german-court-rules' },
      { date: '2022-09-05', text: 'Berlin labour court ruled Farah Maraqa\'s dismissal legally unjustified', source: 'https://web.archive.org/web/2024/https://www.aljazeera.com/news/2022/9/5/german-court-rules-palestinian-ex-dw-journalist-sacking-unlawful' },
      { date: '2023-06-30', text: 'DW\'s appeal rejected, court upheld ruling in favour of Maraqa', source: 'https://web.archive.org/web/2024/https://peoplesdispatch.org/2023/06/30/german-court-rules-in-favor-of-palestinian-journalist-dismissed-by-dw-over-allegations-of-anti-semitism/' }
    ],
    sources: [
      { name: 'Al Jazeera: \'We are scapegoats\' — Arab journalists fired by Deutsche Welle', url: 'https://web.archive.org/web/2024/https://www.aljazeera.com/news/2022/2/11/career-assassination-dws-scapegoating-of-arab-employees' },
      { name: 'Electronic Intifada: Deutsche Welle illegally fired Palestinian journalist', url: 'https://web.archive.org/web/2024/https://electronicintifada.net/blogs/ali-abunimah/deutsche-welle-illegally-fired-palestinian-journalist-german-court-rules' },
      { name: 'Electronic Intifada: Court orders DW to reinstate second journalist', url: 'https://web.archive.org/web/2024/https://electronicintifada.net/blogs/ali-abunimah/court-orders-deutsche-welle-reinstate-second-palestinian-journalist' },
      { name: 'Middle East Eye: Ruling in favour of fired journalist a \'relief\'', url: 'https://web.archive.org/web/2024/https://www.middleeasteye.net/news/germany-deutsche-welle-palestinian-journalist-ruling-relief' },
      { name: 'ELSC: Berlin court finds DW unlawfully dismissed Farah Maraqa', url: 'https://web.archive.org/web/2024/https://elsc.support/cases/berlin-court-finds-deutsche-welle-unlawfully-dismissed-journalist-farah-maraqa/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['ihra']
  },
  {
    id: 'ap-cnn-eslaiah-firing-2023',
    slug: 'ap-cnn-eslaiah-firing',
    title: 'AP and CNN cut ties with Gaza freelancer after October 7 coverage',
    date: '2023-11-08',
    country: 'United States',
    countryCode: 'US',
    institution: 'Associated Press / CNN',
    institutionType: 'Media',
    description: 'The Associated Press and CNN severed ties with Gaza freelance photographer Hassan Eslaiah after HonestReporting published a report alleging Gaza-based photographers had advance knowledge of the October 7 attack. All four news organisations named in the report — AP, Reuters, NYT, and CNN — denied prior knowledge. The Israeli government amplified the report and demanded answers from the outlets. Eslaiah was later targeted and wounded in an Israeli airstrike, with the IOF claiming he was a Hamas member.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'HonestReporting published \'Broken Borders\' alleging Gaza-based photographers had advance knowledge of the October 7 attack. The Israeli government amplified the report and demanded explanations from four international outlets.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'AP, Reuters, NYT, and CNN all issued statements denying prior knowledge. The Daily Beast reported the claims were unsubstantiated. Press freedom organisations noted the allegations put Palestinian journalists at physical risk.',
    updates: [
      { date: '2023-11-08', text: 'HonestReporting published \'Broken Borders\' report targeting Gaza photographers', source: 'https://web.archive.org/web/2024/https://www.cnn.com/2023/11/09/media/news-outlets-deny-prior-knowledge-of-hamas-attack/index.html' },
      { date: '2023-11-09', text: 'AP and CNN confirmed severing ties with Hassan Eslaiah', source: 'https://web.archive.org/web/2024/https://www.washingtonpost.com/style/media/2023/11/09/cnn-ap-photographer-hamas/' },
      { date: '2023-11-09', text: 'Israeli government demanded answers from international media outlets', source: 'https://web.archive.org/web/2024/https://www.timesofisrael.com/israel-demands-clarification-from-global-media-over-photographers-during-hamas-assault/' }
    ],
    sources: [
      { name: 'Washington Post: CNN, AP cut ties with photographer', url: 'https://web.archive.org/web/2024/https://www.washingtonpost.com/style/media/2023/11/09/cnn-ap-photographer-hamas/' },
      { name: 'CNN: News outlets deny prior knowledge of Hamas attack', url: 'https://web.archive.org/web/2024/https://www.cnn.com/2023/11/09/media/news-outlets-deny-prior-knowledge-of-hamas-attack/index.html' },
      { name: 'The Daily Beast: News agencies deny viral report', url: 'https://web.archive.org/web/2024/https://www.thedailybeast.com/news-agencies-deny-viral-report-claiming-journalists-knew-hamas-was-going-to-attack/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'reuters-abu-mustafa-investigation-2024',
    slug: 'reuters-abu-mustafa-investigation',
    title: 'Reuters investigates and clears Gaza photographer Ibraheem Abu Mustafa',
    date: '2024-01-15',
    country: 'United Kingdom',
    countryCode: 'GB',
    institution: 'Reuters',
    institutionType: 'Media',
    description: 'Reuters placed Gaza-based photographer Ibraheem Abu Mustafa under internal investigation following the broader HonestReporting campaign alleging connections between Palestinian journalists and Hamas. After its investigation, Reuters found no evidence to support the claims and retained him.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'Part of the broader campaign following October 7 to discredit Palestinian journalists covering the war on Gaza.',
    currentState: 'Overturned',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'Reuters cleared Abu Mustafa after finding no evidence to support allegations.',
    updates: [
      { date: '2024-01-15', text: 'Reuters placed Abu Mustafa under investigation following pressure campaign' },
      { date: '2024-03-01', text: 'Reuters cleared Abu Mustafa, retaining him as a contributor' }
    ],
    sources: [
      { name: 'Washington Post: CNN, AP cut ties with photographer (broader campaign)', url: 'https://web.archive.org/web/2024/https://www.washingtonpost.com/style/media/2023/11/09/cnn-ap-photographer-hamas/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'ap-reuters-hatem-ali-investigation-2024',
    slug: 'ap-reuters-hatem-ali-investigation',
    title: 'AP and Reuters investigate Gaza photographer Hatem Ali over October 7 allegations',
    date: '2024-01-20',
    country: 'United States',
    countryCode: 'US',
    institution: 'Associated Press / Reuters',
    institutionType: 'Media',
    description: 'Multiple wire services investigated Gaza-based photographer Hatem Ali following allegations linking him to the October 7 attack. Ali was one of four photographers named in HonestReporting\'s original \'Broken Borders\' report. The investigations were part of a wider campaign targeting Palestinian photojournalists who documented events on and after 7 October.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'Continued fallout from the HonestReporting campaign alleging Palestinian journalists had prior knowledge of the October 7 attack.',
    currentState: 'Developing',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'Press freedom organisations condemned the targeting of journalists covering a conflict zone.',
    updates: [
      { date: '2024-01-20', text: 'AP and Reuters confirmed internal investigations into Gaza photographer Hatem Ali' }
    ],
    sources: [
      { name: 'CNN: News outlets deny prior knowledge of Hamas attack', url: 'https://web.archive.org/web/2024/https://www.cnn.com/2023/11/09/media/news-outlets-deny-prior-knowledge-of-hamas-attack/index.html' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'cnn-sabbah-firing-2024',
    slug: 'cnn-sabbah-firing',
    title: 'CNN fires Gaza freelancer Abdel Qader Sabbah',
    date: '2024-07-03',
    country: 'United States',
    countryCode: 'US',
    institution: 'CNN',
    institutionType: 'Media',
    description: 'CNN terminated its relationship with Gaza-based freelancer Abdel Qader Sabbah after HonestReporting published an investigation into his social media history. CNN said it was not aware of his historical social media posts but acknowledged they were \'highly offensive\'. Sabbah had provided material used in CNN stories over the previous nine months, during which CNN\'s own journalists were barred from entering Gaza independently. HonestReporting claimed Sabbah was the 11th journalist reassigned, suspended, or fired due to its campaigns since August 2022.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'HonestReporting published an investigation flagging Sabbah\'s social media history and alleged links to Hamas-run institutions.',
    currentState: 'Currently in effect',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'CNN said it had reviewed Sabbah\'s material and was comfortable it met editorial standards, but would no longer use his work due to the social media posts.',
    updates: [
      { date: '2024-07-03', text: 'HonestReporting published investigation into CNN freelancer Sabbah' },
      { date: '2024-07-04', text: 'CNN confirmed termination of freelance relationship with Sabbah', source: 'https://web.archive.org/web/2024/https://www.jns.org/cnn-fires-pro-hamas-freelancer-after-media-watchdog-expose/' }
    ],
    sources: [
      { name: 'JNS: CNN fires freelancer after watchdog expose', url: 'https://web.archive.org/web/2024/https://www.jns.org/cnn-fires-pro-hamas-freelancer-after-media-watchdog-expose/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  },
  {
    id: 'reuters-pulitzer-campaign-2024',
    slug: 'reuters-pulitzer-campaign',
    title: 'Pressure campaign targets Pulitzer Prize-winning Reuters Gaza coverage',
    date: '2024-05-06',
    country: 'United States',
    countryCode: 'US',
    institution: 'Reuters / Pulitzer Prize Board',
    institutionType: 'Media',
    description: 'After Reuters photographers covering Gaza were awarded the 2024 Pulitzer Prize for Breaking News Photography, HonestReporting launched a campaign to discredit the award, alleging that photographers on the team had crossed into Israel on October 7 or had links to Hamas. Reuters denied the claims and said images from that day were deliberately excluded from the submission. The Pulitzer Board stood by its decision.',
    pressuredBy: ['honest-reporting'],
    pressureDetail: 'HonestReporting published multiple articles calling on the Pulitzer Board to revoke the award, alleging the Reuters Gaza team was \'compromised\'.',
    currentState: 'Overturned',
    tags: ['Journalist targeting', 'Media suppression'],
    response: 'The Pulitzer Board stood by its award. Reuters denied the allegations. Press freedom organisations globally condemned the campaign as an attempt to suppress documentation of the war on Gaza.',
    updates: [
      { date: '2024-05-06', text: 'Reuters awarded 2024 Pulitzer Prize for Breaking News Photography for Gaza coverage', source: 'https://web.archive.org/web/2024/https://www.poynter.org/reporting-editing/2024/reuters-pulitzer-prize-breaking-news-photography-israel-hamas-gaza/' },
      { date: '2024-05-09', text: 'HonestReporting launched campaign to discredit the award' },
      { date: '2024-06-15', text: 'Pulitzer Board confirmed it stood by the award' }
    ],
    sources: [
      { name: 'Poynter: Reuters wins Pulitzer for Israel-Gaza photography', url: 'https://web.archive.org/web/2024/https://www.poynter.org/reporting-editing/2024/reuters-pulitzer-prize-breaking-news-photography-israel-hamas-gaza/' },
      { name: 'PetaPixel: AP and Reuters win 2024 Pulitzer Prizes', url: 'https://web.archive.org/web/2024/https://petapixel.com/2024/05/09/ap-and-reuters-win-2024-pulitzer-prizes-for-powerful-and-tragic-photos/' }
    ],
    relatedMaterials: [],
    relatedOrgs: ['honest-reporting']
  }
];

// ===== HELPER FUNCTIONS =====

export const getOrgById = (id) => orgsData.find(org => org.id === id);
export const getOrgBySlug = (slug) => orgsData.find(org => org.slug === slug);
export const getMaterialBySlug = (slug) => materialsData.find(m => m.slug === slug);
export const getIncidentBySlug = (slug) => incidentsData.find(i => i.slug === slug);

export const getMaterialsForOrg = (orgId) =>
  materialsData.filter(m => m.orgId === orgId);

export const getIncidentsForOrg = (orgId) =>
  incidentsData.filter(i =>
    i.relatedOrgs.includes(orgId) || i.pressuredBy.includes(orgId)
  );

export const getOrgCountries = () => {
  const countries = [...new Set(orgsData.map(o => o.country))];
  return countries.sort();
};

export const getIncidentCountries = () => {
  const countries = [...new Set(incidentsData.map(i => i.country))];
  return countries.sort();
};

export const getDateDecade = (dateStr) => {
  const year = parseInt(dateStr);
  if (isNaN(year)) return 'Unknown';
  if (year >= 2020) return '2020s';
  if (year >= 2010) return '2010s';
  if (year >= 2000) return '2000s';
  if (year >= 1990) return '1990s';
  return 'Before 1990';
};
